import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Smartphone, Home, Settings } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { UserProfile } from './UserProfile'
import { LoginButton } from './LoginButton'
import { getVersionInfo } from '../config/version'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth()
  const versionInfo = getVersionInfo()

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Smartphone className="w-8 h-8 text-brand-600" />
                <h1 className="text-xl font-bold text-gray-900">BrandedApp</h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {isAuthenticated && (
                <nav className="hidden md:flex space-x-1">
                  <Link
                    to="/"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive('/') 
                        ? 'bg-brand-100 text-brand-700' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Home className="w-4 h-4 inline mr-2" />
                    Dashboard
                  </Link>
                  <Link
                    to="/settings/stores"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/settings') 
                        ? 'bg-brand-100 text-brand-700' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Settings className="w-4 h-4 inline mr-2" />
                    Store Settings
                  </Link>
                </nav>
              )}
              
              {isAuthenticated ? <UserProfile /> : <LoginButton />}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-sm text-gray-500">
            BrandedApp Generator v{versionInfo.version} - {versionInfo.environment} ({versionInfo.buildDate})
          </div>
        </div>
      </footer>
    </div>
  )
} 